import { useState } from "react";
import { Button, EnvBadge, ErrorNotice, Field, Input, Loading, Panel, useToast } from "../../components/ui";
import { gameApi } from "../../lib/api";
import { formatMoney } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { useClient } from "../../lib/session";
import type { GameTabProps } from "./Game";

export function GameCurrencies({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const toast = useToast();
  const q = useQuery(`dev:currencies:${game.id}:${env}`, () => api.currencies());
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [exponent, setExponent] = useState(0);
  const create = useAction(() => api.createCurrency({ code: code.trim(), name: name.trim(), symbol: symbol.trim() || undefined, exponent }));
  const custom = (q.data?.data ?? []).filter((c) => !c.standard);
  const standard = (q.data?.data ?? []).filter((c) => c.standard);
  const sample = 1234567;

  return (
    <div className="stack">
      <Panel title={<>Define a <EnvBadge env={env} /> game currency</>} sub="Use your game's own unit (gold, credits, gems) instead of a real currency. Amounts are always whole numbers of the smallest unit.">
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            const c = await create.run();
            if (c) {
              setCode("");
              setName("");
              setSymbol("");
              invalidate(`dev:currencies:${game.id}`);
              toast(`Defined ${c.code}`);
            }
          }}
        >
          <div className="form-row">
            <Field label="Code" hint="3–16 capitals, digits or _"><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GOLD" required pattern="[A-Z][A-Z0-9_]{2,15}" /></Field>
            <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gold coins" required /></Field>
            <Field label="Symbol"><Input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="◎" maxLength={8} /></Field>
            <Field label="Decimal places" hint="0 for whole units"><Input type="number" min={0} max={6} value={exponent} onChange={(e) => setExponent(Number(e.target.value))} /></Field>
          </div>
          <p className="small muted">
            {sample.toLocaleString()} minor units will display as {formatMoney(sample, { code: code || "CODE", exponent, symbol: symbol || null })}.
          </p>
          <ErrorNotice error={create.error} />
          <div><Button type="submit" variant="primary" busy={create.pending}>Define currency</Button></div>
        </form>
      </Panel>
      <Panel title="Your game currencies" flush>
        <ErrorNotice error={q.error} onRetry={q.reload} />
        {q.loading && !q.data && <Loading />}
        {q.data && custom.length === 0 && <p className="muted panel-body">None yet in {env}.</p>}
        {custom.length > 0 && (
          <table className="data">
            <thead><tr><th>Code</th><th>Name</th><th>Symbol</th><th className="num">Decimal places</th></tr></thead>
            <tbody>{custom.map((c) => <tr key={c.id}><td className="mono">{c.code}</td><td>{c.name}</td><td>{c.symbol}</td><td className="num">{c.exponent}</td></tr>)}</tbody>
          </table>
        )}
      </Panel>
      <Panel title="Standard currencies" sub="ISO 4217 codes available to every game." flush>
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Code</th><th>Name</th><th>Symbol</th><th className="num">Decimal places</th></tr></thead>
            <tbody>{standard.map((c) => <tr key={c.id}><td className="mono">{c.code}</td><td>{c.name}</td><td>{c.symbol}</td><td className="num">{c.exponent}</td></tr>)}</tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
